import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { SynopticNodeData } from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import { SynopticPlanItem } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    LayerBroadCastChannelData,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { HomeService } from '@connect-services/client/services/home.service';
import { DepositWithStandard } from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';
import { MapSynopticRedirectionService } from '@connect-services/client/services/synoptic/map-synoptic-redirection.service';
import {
    SelectedSynoptic,
    SelectSynopticService,
} from '@connect-services/client/services/synoptic/select-synoptic.service';
import { GraceThdSynopticItem, SynopticZone } from '@connect-services/src/sdk/api';

export type SynopticBoxPlanData = {
    cyJson: JSON | null;
    isLoading: boolean;
    planItems: SynopticPlanItem[] | null;
    selectedDeposit: DepositWithStandard | null;
    selectedNodeData: SynopticNodeData | null;
    selectedSynoptic: SelectedSynoptic | null;
};

export type SynopticData = {
    isSynopticDisplayed: boolean;
    selectedDeposit: DepositWithStandard | null;
    selectedSynoptic: SelectedSynoptic | null;
    selectedSynopticZone: SynopticZone | null;
    synopticZones: SynopticZone[];
};

export type SynopticMapData = {
    depositId: string;
    layerStyleType: LayerStyleTypeEnum;
    node: string;
    synopticItem?: GraceThdSynopticItem;
};

export interface NetworkPathRedirectionData {
    entityId: string;
    layerId: string;
    positionId?: string;
    projectId: string;
}

@Injectable({
    providedIn: 'root',
})
export class BroadcastChannelService {
    public readonly currentLayer: WritableSignal<string | null> = signal(null);
    public readonly externalOpenInProgress = signal(false);
    public readonly isSynopticOpenInExternalPage = signal(false);

    private readonly homeService = inject(HomeService);
    private readonly lastSynopticSnapshot = signal<SynopticData | null>(null);
    private readonly mapSynopticRedirectionService = inject(MapSynopticRedirectionService);
    private readonly navigationBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.NAVIGATION);
    private readonly pendingSynopticRedirection = signal<LayerBroadCastChannelData | null>(null);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly selectSynopticService = inject(SelectSynopticService);

    public constructor() {
        effect(() => {
            this.homeService.synopticExtPageOpen.set(this.isSynopticOpenInExternalPage());
        });
        this.listenNetworkPathRedirection();
    }

    public checkIfLayerWindowExists(layerName: string): Promise<boolean> {
        const channel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);

        const RESPONSE_TIMEOUT_MS = 200;

        return new Promise<boolean>((resolve) => {
            let resolved = false;

            const cleanup = (): void => {
                channel.close();
            };

            const timeout = setTimeout(() => {
                if (resolved) {
                    return;
                }

                resolved = true;
                cleanup();
                resolve(false);
            }, RESPONSE_TIMEOUT_MS);

            channel.onmessage = (event: MessageEvent<BroadcastChannelEvent<string>>): void => {
                const data = event.data;

                if (
                    data.message !== BroadcastChannelMessageEnum.LAYER_WINDOW_EXISTS ||
                    data.additionalData !== layerName
                ) {
                    return;
                }

                if (resolved) {
                    return;
                }

                resolved = true;
                clearTimeout(timeout);
                cleanup();
                resolve(true);
            };

            channel.postMessage({
                additionalData: layerName,
                message: BroadcastChannelMessageEnum.REQUEST_LAYER_WINDOW,
                source: BroadcastChannelSourceEnum.LAYER,
            });
        });
    }

    public closeAllExternalWindows(): void {
        for (const channelName of Object.values(BroadcastChannelNameEnum)) {
            const broadcastChannel: BroadcastChannel = new BroadcastChannel(channelName);

            broadcastChannel.postMessage(<BroadcastChannelEvent<null>>{
                message: BroadcastChannelMessageEnum.CLOSE,
                source: BroadcastChannelSourceEnum.HOMEPAGE,
            });

            broadcastChannel.close();
        }
    }

    public closeExternalWindowBP(broadcastChannel: BroadcastChannel): void {
        broadcastChannel.postMessage(<BroadcastChannelEvent<null>>{
            isClosing: true,
            source: BroadcastChannelSourceEnum.SYNOPTIC_PLAN,
        });
    }

    public closeExternalWindowSynoptic(broadcastChannel: BroadcastChannel): void {
        broadcastChannel.postMessage(<BroadcastChannelEvent<SynopticData>>{
            isClosing: true,
            message: BroadcastChannelMessageEnum.CLOSE,
            source: BroadcastChannelSourceEnum.GRAPH,
        });
    }

    public handleOpenInNewWindow(endPath: string): void {
        this.externalOpenInProgress.set(true);
        this.snapshotSynopticSelection();
        this.openInNewWindow(`${document.URL}/${endPath}`, undefined, endPath);
    }

    public async moveMapOnMessage(data: BroadcastChannelEvent<SynopticMapData>): Promise<void> {
        const properties = data.additionalData;

        if (!properties) {
            return;
        }

        await this.mapSynopticRedirectionService.moveMapToEntityLocationFromSynopticNode(
            properties.node,
            properties.depositId,
            properties.layerStyleType,
            properties.synopticItem,
        );
    }

    public openInNewWindow(url: string, queryParams?: Record<string, string>, targetName?: string): void {
        if (queryParams) {
            const params = new URLSearchParams();
            for (const [key, value] of Object.entries(queryParams)) {
                params.set(key, String(value));
            }
            url += `?${params.toString()}`;
        }

        window.open(url, targetName ?? '_blank', 'location=yes,scrollbars=yes,status=yes');
    }

    public openSelectedSynoptic(data: BroadcastChannelEvent<SynopticData | null>): void {
        const properties = data.additionalData;

        if (!properties) {
            return;
        }

        this.selectSynopticService.isSynopticDisplayed.set(properties.isSynopticDisplayed);
        this.selectSynopticService.selectedDeposit.set(properties.selectedDeposit);
        this.selectSynopticService.selectedSynoptic.set(properties.selectedSynoptic);
        this.selectSynopticService.selectedSynopticZone.set(properties.selectedSynopticZone);
        this.selectSynopticService.synopticZones.set(properties.synopticZones);
    }

    public openSelectedSynopticOnNewWindow(broadcastChannel: BroadcastChannel): void {
        const synopticData = this.lastSynopticSnapshot() ?? this.getDataFromSelectSynopticService();

        broadcastChannel.postMessage(<BroadcastChannelEvent<SynopticData>>{
            additionalData: synopticData,
            isOpening: true,
            source: BroadcastChannelSourceEnum.HOMEPAGE,
        });

        const pendingRedirection = this.pendingSynopticRedirection();
        if (!pendingRedirection) {
            return;
        }

        const layersBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
        layersBroadcastChannel.postMessage(<BroadcastChannelEvent<LayerBroadCastChannelData>>{
            additionalData: pendingRedirection,
            message: BroadcastChannelMessageEnum.MOVE_SYNOPTIC,
            source: BroadcastChannelSourceEnum.LAYER,
        });
        layersBroadcastChannel.close();
        this.pendingSynopticRedirection.set(null);
    }

    public openSynopticInExternalWindow(redirection?: LayerBroadCastChannelData): void {
        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        this.externalOpenInProgress.set(true);
        this.pendingSynopticRedirection.set(redirection ?? null);
        this.snapshotSynopticSelection();

        const urlTree = this.router.createUrlTree([
            RoutePathEnum.PROJECTS,
            projectId,
            RoutePathEnum.SYNOPTIC,
            RoutePathEnum.EXT,
        ]);

        this.openInNewWindow(
            `${window.location.origin}${this.router.serializeUrl(urlTree)}`,
            undefined,
            RoutePathEnum.EXT,
        );
    }

    public redirectNetworkPathOnHomepage(
        projectId: string,
        layerId: string,
        entityId: string,
        positionId?: string,
    ): void {
        const broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.NAVIGATION);

        broadcastChannel.postMessage(<BroadcastChannelEvent<NetworkPathRedirectionData>>{
            additionalData: {
                entityId,
                layerId,
                positionId,
                projectId,
            },
            message: BroadcastChannelMessageEnum.OPEN_NETWORK_PATH,
            source: BroadcastChannelSourceEnum.NETWORK_PATH,
        });

        broadcastChannel.close();
    }

    public snapshotSynopticSelection(): void {
        this.lastSynopticSnapshot.set(this.getDataFromSelectSynopticService());
    }

    public updateTabsEntities(broadcastChannel: BroadcastChannel, selectedFilters: string[]): void {
        broadcastChannel.postMessage(<BroadcastChannelEvent<string[]>>{
            additionalData: selectedFilters,
            message: BroadcastChannelMessageEnum.APPLY_FILTERS_SUCCESS,
            source: BroadcastChannelSourceEnum.TABLE_VIEW,
        });
    }

    private getDataFromSelectSynopticService(): SynopticData {
        return {
            isSynopticDisplayed: this.selectSynopticService.isSynopticDisplayed(),
            selectedDeposit: this.selectSynopticService.selectedDeposit(),
            selectedSynoptic: this.selectSynopticService.selectedSynoptic(),
            selectedSynopticZone: this.selectSynopticService.selectedSynopticZone(),
            synopticZones: this.selectSynopticService.synopticZones(),
        };
    }

    private listenNetworkPathRedirection(): void {
        this.navigationBroadcastChannel.onmessage = async (
            event: MessageEvent<BroadcastChannelEvent<NetworkPathRedirectionData>>,
        ): Promise<void> => {
            const data = event.data;

            if (
                this.homeService.isExternalPage() ||
                data.message !== BroadcastChannelMessageEnum.OPEN_NETWORK_PATH ||
                data.source !== BroadcastChannelSourceEnum.NETWORK_PATH ||
                !data.additionalData
            ) {
                return;
            }

            const { projectId, layerId, entityId, positionId } = data.additionalData;

            await this.router.navigate(
                [
                    RoutePathEnum.PROJECTS,
                    projectId,
                    RoutePathEnum.LAYERS,
                    layerId,
                    RoutePathEnum.NETWORK_PATH,
                    entityId,
                ],
                {
                    queryParams: positionId ? { positionId } : undefined,
                },
            );
        };
    }
}
